import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { RefreshCw, FileWarning } from 'lucide-react';
import { Button } from '../common/ui';
import { saveBlob, blobErrorMessage } from '../../utils/downloadBlob';
import { ResolveForm, actionsFor } from './GkResolve';
import type { GkDrift as GkDriftData, GkFinding } from './types';

/**
 * Exported documents whose data changed after the export (GK_EXPORT_PLAN.md
 * 5.2 and 5.4, milestone M11). An open period: cancel the export in the
 * history and export the day again. A closed period: make a correction
 * (storno + replacement).
 */

const inputClass =
  'rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500';
const labelClass = 'mb-1 text-xs font-medium text-gray-500';

const REASON: Record<string, string> = {
  changed: 'lines changed',
  blocked: 'now held',
  missing: 'payment gone',
  moved: 'moved to another day',
};

/** The month after closed_through ("2026-09" -> 10/2026), else the current month. */
export function defaultPeriod(closedThrough: string | null, today = new Date()): { month: number; year: number } {
  if (closedThrough && /^\d{4}-\d{2}$/.test(closedThrough)) {
    let year = Number(closedThrough.slice(0, 4));
    let month = Number(closedThrough.slice(5, 7)) + 1;
    if (month === 13) {
      month = 1;
      year += 1;
    }
    return { month, year };
  }
  return { month: today.getMonth() + 1, year: today.getFullYear() };
}

const GkDrift: React.FC<{ enabled: boolean; closedThrough: string | null; onCorrected?: () => void; autoCheck?: boolean }> = ({
  enabled, closedThrough, onCorrected, autoCheck = false,
}) => {
  const [drift, setDrift] = useState<GkDriftData | null>(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<number[]>([]);
  const initial = defaultPeriod(closedThrough);
  const [month, setMonth] = useState(initial.month);
  const [year, setYear] = useState(initial.year);
  const [correcting, setCorrecting] = useState(false);
  const [message, setMessage] = useState('');
  // Findings of a refused correction (409 details.problems[].findings).
  const [refused, setRefused] = useState<GkFinding[]>([]);
  const [resolving, setResolving] = useState<string | null>(null);

  const check = useCallback(async () => {
    setChecking(true);
    setError('');
    try {
      const res = await axios.post<{ drift: GkDriftData | null }>('/api/accounting/gk-export/drift/check', { trigger: 'gk_page' });
      setDrift(res.data.drift);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'The change check failed.');
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    if (autoCheck) check();
    else {
      axios.get<{ drift: GkDriftData | null }>('/api/accounting/gk-export/drift')
        .then((res) => setDrift(res.data.drift))
        .catch(() => {});
    }
  }, [autoCheck, check]);

  const toggle = (id: number) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const correct = async () => {
    setCorrecting(true);
    setError('');
    setMessage('');
    setRefused([]);
    try {
      const res = await axios.post<{ batchId: number; fileName: string; documents: Array<{ dokument: string }> }>(
        '/api/accounting/gk-export/corrections',
        { documentIds: selected, periodMonth: month, periodYear: year }
      );
      setMessage(`Correction written: ${res.data.documents.map((d) => d.dokument).join(', ')}.`);
      setSelected([]);
      if (onCorrected) onCorrected();
      // Check first: check() clears the error, and a download error must stay visible.
      await check();
      try {
        const file = await axios.get(`/api/accounting/gk-export/exports/${res.data.batchId}/file`, { responseType: 'blob' });
        saveBlob(file.data, res.data.fileName);
      } catch (err: any) {
        const text = await blobErrorMessage(err, 'The correction is stored, but the download failed. Download it from the history.');
        setError((previous) => (previous ? `${previous} ${text}` : text));
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || 'The correction failed.');
      const problems: Array<{ findings?: GkFinding[] }> = err?.response?.data?.problems || [];
      setRefused(problems.flatMap((p) => p.findings || []));
    } finally {
      setCorrecting(false);
    }
  };

  const docs = drift?.documents || [];
  const keyOf = (f: GkFinding) => `${f.paymentId}|${f.itemKey}|${f.code}`;
  // A decision for a held payment of an exported document: it is used only by
  // the correction (normal exports keep the exported lines).
  const decisions = (findings: GkFinding[] | undefined) =>
    (findings || []).filter((f) => actionsFor(f).length > 0).map((f) => (
      <span key={keyOf(f)} className="block">
        <span className="text-gray-700">{f.code}: {f.message}</span>
        {enabled && (
          <button
            type="button"
            className="ml-2 rounded border border-blue-300 px-1.5 text-blue-700 hover:bg-blue-50"
            onClick={() => setResolving(resolving === keyOf(f) ? null : keyOf(f))}
          >
            Resolve
          </button>
        )}
        {resolving === keyOf(f) && (
          <ResolveForm
            finding={f}
            onClose={() => setResolving(null)}
            onSaved={() => {
              setResolving(null);
              setRefused((r) => r.filter((x) => keyOf(x) !== keyOf(f)));
              check();
            }}
          />
        )}
      </span>
    ));
  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center gap-3 text-sm">
        <Button variant="secondary" icon={RefreshCw} onClick={check} disabled={checking}>
          {checking ? 'Checking…' : 'Check for changes'}
        </Button>
        <span className="text-gray-600">
          {drift
            ? `Last check ${new Date(drift.checkedAt).toLocaleString('sl-SI')}: ${drift.checkedDocuments} exported document${drift.checkedDocuments === 1 ? '' : 's'}, ${docs.length} changed.`
            : 'Not checked yet.'}
        </span>
      </div>
      {error && <p className="mb-2 text-sm text-red-700">{error}</p>}
      {refused.length > 0 && <div className="mb-2 text-xs">{decisions(refused)}</div>}
      {message && <p className="mb-2 text-sm text-green-700">{message}</p>}
      {docs.length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm">
          <p className="mb-2 text-gray-700">
            These documents no longer match the data. If AJMP can still delete the journal (open VAT period), cancel the export in the history and export
            the day again. If the period is closed, make a correction: a storno of the document and a new document, in the chosen open period.
          </p>
          <ul className="mb-3 space-y-1">
            {docs.map((d) => (
              <li key={d.id}>
                <label className="flex items-start gap-2">
                  <input type="checkbox" checked={selected.includes(d.id)} onChange={() => toggle(d.id)} aria-label={`Correct ${d.dokument}`} />
                  <span>
                    <FileWarning className="mr-1 inline h-4 w-4 text-amber-700" />
                    <strong>{d.dokument}</strong> ({d.day}, {d.currency}){d.settingsChanged && <span className="ml-1 text-xs text-gray-500">settings changed since the export</span>}
                    <span className="block text-xs text-gray-600">
                      {d.payments.map((p) => `payment ${p.paymentId}: ${REASON[p.reason] || p.reason}${p.detail ? ` (${p.detail})` : ''}`).join('; ')}
                    </span>
                  </span>
                </label>
                <div className="ml-6 text-xs">{d.payments.flatMap((p) => decisions(p.findings))}</div>
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col">
              <label htmlFor="corr-month" className={labelClass}>Period month</label>
              <input id="corr-month" type="number" min={1} max={12} value={month} onChange={(e) => setMonth(Number(e.target.value))} className={`${inputClass} w-20`} />
            </div>
            <div className="flex flex-col">
              <label htmlFor="corr-year" className={labelClass}>Year</label>
              <input id="corr-year" type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} className={`${inputClass} w-24`} />
            </div>
            <Button onClick={correct} disabled={!enabled || correcting || selected.length === 0}>
              {correcting ? 'Writing…' : `Make correction (${selected.length})`}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GkDrift;
